import { useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { bold, num, tableRows } from "../../utils/xlsx";
import { download, ExportButton, fileName, rangeLabel, sheetHeading } from "./excel";

const OUTPUT_TAX_COLUMNS = [
  { key: "taxRateName", label: "Tax Rate" },
  { key: "ratePercentage", label: "Rate %" },
  { key: "taxType", label: "Type" },
  { key: "taxableValue", label: "Taxable Value", format: "money" },
  { key: "taxAmount", label: "Tax Amount", format: "money" },
];

const money = (value) => Number(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GstReport = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [gstin, setGstin] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState({ fromDate: "", toDate: "" });

  const exportExcel = () => {
    const filters = `${rangeLabel(applied.fromDate, applied.toDate)}${report.clubbedByGstin ? ` — GSTIN ${report.clubbedByGstin}` : ""}`;
    download(fileName("GST Report", rangeLabel(applied.fromDate, applied.toDate)), [
      {
        name: "GST Report",
        rows: [
          ...sheetHeading("GST Report", filters),
          ["Output Tax (Sales)", "", "", "", num(report.totalOutputTax)],
          ["Input Tax (Purchases)", "", "", "", num(report.totalInputTax)],
          [bold("Net GST Payable"), "", "", "", bold(num(report.netGstPayable))],
          [report.inputTaxNote || ""],
          [],
          [bold("Output Tax by Rate")],
          ...tableRows(OUTPUT_TAX_COLUMNS, report.outputTax || [], { totals: true }),
        ],
        widths: [28, 10, 14, 18, 18],
      },
    ]);
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/reports/gst", {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        gstin: gstin.trim() || undefined,
      });
      setReport(data);
      setApplied({ fromDate, toDate });
    } catch (error) {
      toast.error(error.message || "Failed to load GST report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading GST report...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <input type="date" onClick={(e) => e.target.showPicker?.()} value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" onClick={(e) => e.target.showPicker?.()} value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <input
          type="text"
          placeholder="Club by GSTIN — Super Admin only"
          value={gstin}
          onChange={(e) => setGstin(e.target.value)}
          style={{ minWidth: "220px" }}
        />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={loading || !report} />
      </div>
      {gstin.trim() ? (
        <div className="page-subtitle" style={{ marginBottom: "8px" }}>
          Rolling up every distributor registered under GSTIN <strong>{gstin.trim()}</strong> into one combined
          report — leave blank to see only your own distributor.
        </div>
      ) : null}

      {!report ? (
        <div className="empty-state">
          <span className="empty-state-title">Select a period (optional) and click Run Report</span>
        </div>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <div className="stat-card">
              <div className="stat-card-label">Output Tax (Sales)</div>
              <div className="stat-card-value">₹{money(report.totalOutputTax)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Input Tax (Purchases)</div>
              <div className="stat-card-value">₹{money(report.totalInputTax)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Net GST Payable</div>
              <div className="stat-card-value">₹{money(report.netGstPayable)}</div>
            </div>
          </div>

          <div className="page-subtitle" style={{ marginBottom: "8px" }}>{report.inputTaxNote}</div>
          {report.clubbedByGstin ? (
            <div className="page-subtitle" style={{ marginBottom: "8px" }}>
              Combined across {report.distributorCount} distributor{report.distributorCount === 1 ? "" : "s"} sharing GSTIN {report.clubbedByGstin}.
            </div>
          ) : null}

          <h4 style={{ fontWeight: 700, marginBottom: "6px" }}>Output Tax by Rate</h4>
          <div className="table-wrapper" style={{ marginTop: 0 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>Tax Rate</th>
                  <th style={{ textAlign: "center" }}>Rate %</th>
                  <th style={{ textAlign: "center" }}>Type</th>
                  <th style={{ textAlign: "center" }}>Taxable Value</th>
                  <th style={{ textAlign: "center" }}>Tax Amount</th>
                </tr>
              </thead>
              <tbody>
                {(report.outputTax || []).length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="empty-state">
                        <span className="empty-state-title">No taxed sales in this period</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  report.outputTax.map((row) => (
                    <tr key={row.taxRateId}>
                      <td style={{ textAlign: "center" }}>{row.taxRateName}</td>
                      <td style={{ textAlign: "center" }}>{row.ratePercentage}%</td>
                      <td style={{ textAlign: "center" }}>{row.taxType}</td>
                      <td style={{ textAlign: "center" }}>{money(row.taxableValue)}</td>
                      <td style={{ textAlign: "center" }}>{money(row.taxAmount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default GstReport;
